import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { createPortal } from 'react-dom';
import { StyleSheet } from 'react-native-unistyles';
import GuideStage from './GuideStage';

export default function GuideOverlay({ onSkip, ...props }) {
    const host = useRef(null);
    const skipRef = useRef(onSkip);
    skipRef.current = onSkip;
    const [reducedMotion] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true);
    useEffect(() => {
        const previousFocus = document.activeElement;
        const handleKey = event => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                skipRef.current();
            }
            if (event.key !== 'Tab') return;
            const controls = [...(host.current?.querySelectorAll('button, input, [role="button"], [role="switch"], [tabindex="0"]') || [])].filter(node => !node.disabled && node.getAttribute('aria-disabled') !== 'true');
            if (!controls.length) return;
            const index = controls.indexOf(document.activeElement);
            event.preventDefault();
            const nextIndex = index < 0 ? (event.shiftKey ? controls.length - 1 : 0) : (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
            controls[nextIndex]?.focus();
        };
        const background = [...document.body.children].filter(node => !node.contains(host.current));
        const oldInert = background.map(node => node.inert);
        background.forEach(node => { node.inert = true; });
        document.addEventListener('keydown', handleKey, true);
        return () => {
            document.removeEventListener('keydown', handleKey, true);
            background.forEach((node, index) => { node.inert = oldInert[index]; });
            if (previousFocus?.isConnected) previousFocus.focus?.();
        };
    }, []);
    return createPortal(
        <View ref={host} testID="guide-overlay" style={styles.overlay}>
            <GuideStage {...props} onSkip={onSkip} animate={!reducedMotion} />
        </View>, document.body,
    );
}

// Named-export parity with GuideOverlay.native.jsx — hosted overlays only
// exist on native; web always renders through the root portal above.
export const GuideOverlayEmbedded = GuideOverlay;

const styles = StyleSheet.create(() => ({
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999998 },
}));
